import React, { useState, useEffect, useMemo } from "react";
import { functionService } from "../../lib/functions";
import Sidemenu from "../../components/sidemenu";
import { Select, DatePicker, Spin } from "antd";
import dayjs from "dayjs";

import { exportToXLSX } from "./export";
import {
  /* helpers */
  isAdmin,
  getMyName,
  toYMD,
  fmt,
  todayYMD,
  addDays,
  startOfWeek,
  startOfMonth,
  n,
  pct,
  /* palettes & utils */
  CLIENT_PALETTE,
  RECRUITER_PALETTE,
  hashStr,
  clientColor,
  /* styled components */
  GlobalFonts,
  MainDiv,
  ContentWrapDiv,
  TopContainer,
  RangeTabsWrapper,
  RangeTab,
  FilterRow,
  StyledSelectWrap,
  StyledDatePickerWrap,
  FilterRecruiterChip,
  FilterClientChip,
  FilterRoleChip,
  DashScrollArea,
  KPIStrip,
  KPICard,
  KPIValue,
  KPILabel,
  KPISubLabel,
  SectionTitle,
  FunnelGrid,
  StageCard,
  StageHeader,
  StageIcon,
  StageTitle,
  StageBadge,
  StageBody,
  StageMainNum,
  StageMainLabel,
  StageDivider,
  StageDeductions,
  DeductRow,
  DeductLabel,
  DeductVal,
  StageConversion,
  ConvLabel,
  ConvValue,
  DetailPanel,
  DetailTitle,
  AccountList,
  AccountItem,
  AccountName,
  AccountVal,
  ExportBtn,
  SpinWrap,
  EmptyWrap,
  ConnectorArrow,
  KPICountLabel,
  StageBottom,
} from "./styled";

/* ═══════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════ */
const ClientDashboard = () => {
  const admin = isAdmin();
  const myName = getMyName();

  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedStage, setExpandedStage] = useState(null);

  const savedMode =
    localStorage.getItem("client_dash_range_mode") || "yesterday";
  const savedFrom =
    localStorage.getItem("client_dash_from_date") || addDays(-1);
  const savedTo = localStorage.getItem("client_dash_to_date") || addDays(-1);

  const datesFromMode = (mode) => {
    const today = todayYMD();
    const yesterday = addDays(-1);
    if (mode === "today") return { from: today, to: today };
    if (mode === "yesterday") return { from: yesterday, to: yesterday };
    if (mode === "3days") return { from: addDays(-2), to: today };
    if (mode === "week") return { from: startOfWeek(), to: today };
    if (mode === "month") return { from: startOfMonth(), to: today };
    return null;
  };

  const computed = datesFromMode(savedMode);
  const [rangeMode, setRangeMode] = useState(savedMode);
  const [fromDate, setFromDate] = useState(
    computed ? computed.from : savedFrom,
  );
  const [toDate, setToDate] = useState(computed ? computed.to : savedTo);
  const [filterRecruiter, setFilterRecruiter] = useState(null);
  const [filterAccount, setFilterAccount] = useState(null);
  const [filterSubAccount, setFilterSubAccount] = useState(null);
  const [filterRole, setFilterRole] = useState(null);
  const [filterEndClient, setFilterEndClient] = useState(null);

  const fetchDashboard = async (from, to) => {
    setLoading(true);
    try {
      const res = await functionService.get(
        `demand/client-dashboard?from=${from}&to=${to}`,
      );
      setRawData(res?.response?.data?.data ?? []);
    } catch (err) {
      console.error("ClientDashboard fetch error:", err);
      setRawData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    localStorage.setItem("client_dash_range_mode", rangeMode);
    localStorage.setItem("client_dash_from_date", fromDate);
    localStorage.setItem("client_dash_to_date", toDate);
  }, [rangeMode, fromDate, toDate]);

  useEffect(() => {
    fetchDashboard(fromDate, toDate);
  }, [fromDate, toDate]);

  const filteredData = useMemo(() => {
    let d = rawData;
    if (!admin) d = d.filter((r) => r.recruiter_name === myName);
    if (filterRecruiter)
      d = d.filter((r) => r.recruiter_name === filterRecruiter);
    if (filterAccount) d = d.filter((r) => r.account === filterAccount);
    if (filterSubAccount)
      d = d.filter((r) => r.sub_account === filterSubAccount);
    if (filterRole) d = d.filter((r) => r.role_title === filterRole);
    if (filterEndClient) d = d.filter((r) => r.end_client === filterEndClient);
    return d;
  }, [
    rawData,
    admin,
    myName,
    filterRecruiter,
    filterAccount,
    filterSubAccount,
    filterRole,
    filterEndClient,
  ]);

  /* ── filter option lists ── */
  const baseData = useMemo(() => {
    let d = rawData;
    if (!admin) d = d.filter((r) => r.recruiter_name === myName);
    return d;
  }, [rawData, admin, myName]);

  const recruiterOptions = useMemo(() => {
    let d = baseData;
    if (filterAccount) d = d.filter((r) => r.account === filterAccount);
    if (filterSubAccount)
      d = d.filter((r) => r.sub_account === filterSubAccount);
    if (filterRole) d = d.filter((r) => r.role_title === filterRole);
    if (filterEndClient) d = d.filter((r) => r.end_client === filterEndClient);
    return [...new Set(d.map((r) => r.recruiter_name))].filter(Boolean).sort();
  }, [baseData, filterAccount, filterSubAccount, filterRole, filterEndClient]);

  const accountOptions = useMemo(() => {
    let d = baseData;
    if (filterRecruiter)
      d = d.filter((r) => r.recruiter_name === filterRecruiter);
    if (filterSubAccount)
      d = d.filter((r) => r.sub_account === filterSubAccount);
    if (filterRole) d = d.filter((r) => r.role_title === filterRole);
    if (filterEndClient) d = d.filter((r) => r.end_client === filterEndClient);
    return [...new Set(d.map((r) => r.account))].filter(Boolean).sort();
  }, [
    baseData,
    filterRecruiter,
    filterSubAccount,
    filterRole,
    filterEndClient,
  ]);

  const subAccountOptions = useMemo(() => {
    let d = baseData;
    if (filterRecruiter)
      d = d.filter((r) => r.recruiter_name === filterRecruiter);
    if (filterAccount) d = d.filter((r) => r.account === filterAccount);
    if (filterRole) d = d.filter((r) => r.role_title === filterRole);
    if (filterEndClient) d = d.filter((r) => r.end_client === filterEndClient);
    return [...new Set(d.map((r) => r.sub_account))].filter(Boolean).sort();
  }, [baseData, filterRecruiter, filterAccount, filterRole, filterEndClient]);

  const roleOptions = useMemo(() => {
    let d = baseData;
    if (filterRecruiter)
      d = d.filter((r) => r.recruiter_name === filterRecruiter);
    if (filterAccount) d = d.filter((r) => r.account === filterAccount);
    if (filterSubAccount)
      d = d.filter((r) => r.sub_account === filterSubAccount);
    if (filterEndClient) d = d.filter((r) => r.end_client === filterEndClient);
    return [...new Set(d.map((r) => r.role_title))].filter(Boolean).sort();
  }, [
    baseData,
    filterRecruiter,
    filterAccount,
    filterSubAccount,
    filterEndClient,
  ]);

  const endClientOptions = useMemo(() => {
    let d = baseData;
    if (filterRecruiter)
      d = d.filter((r) => r.recruiter_name === filterRecruiter);
    if (filterAccount) d = d.filter((r) => r.account === filterAccount);
    if (filterSubAccount)
      d = d.filter((r) => r.sub_account === filterSubAccount);
    if (filterRole) d = d.filter((r) => r.role_title === filterRole);
    return [...new Set(d.map((r) => r.end_client))].filter(Boolean).sort();
  }, [baseData, filterRecruiter, filterAccount, filterSubAccount, filterRole]);

  const recruiterColorMap = useMemo(() => {
    const allNames = [...new Set(baseData.map((r) => r.recruiter_name))]
      .filter(Boolean)
      .sort();
    const map = {};
    allNames.forEach((name, i) => {
      map[name] = RECRUITER_PALETTE[i % RECRUITER_PALETTE.length];
    });
    return map;
  }, [rawData]);

  /* ── aggregate totals ── */
  const sum = (key) => filteredData.reduce((acc, r) => acc + n(r[key]), 0);

  const G = useMemo(
    () => ({
      openings:                   sum("openings"),
      profile_sourced:            sum("profile_sourced"),
      assessed:                   sum("assessed"),
      assessed_cleared:           sum("assessed_cleared"),
      sent_to_client:             sum("sent_to_client"),
      duplicate:                  sum("duplicate"),
      change_in_requirement:      sum("change_in_requirement"),
      position_on_hold_closed:    sum("position_on_hold_closed"),
      yet_to_be_shortlisted:      sum("yet_to_be_shortlisted"),
      screen_reject:              sum("screen_reject"),

      // Global back-outs (screening stage)
      pre_interview_back_out:     sum("pre_interview_back_out"),
      post_interview_back_out:    sum("post_interview_back_out"),

      // R1
      screen_select:              sum("screen_select"),           // "Screen Select - Yet to be Interviewed (R1)"
      interview_scheduled_r1:     sum("interview_scheduled_r1"),
      during_interview_back_out_r1: sum("during_interview_back_out_r1"),
      feedback_pending_r1:        sum("feedback_pending_r1"),
      rejected_r1:                sum("rejected_r1"),

      // R2
      yet_to_be_interviewed_r2:   sum("yet_to_be_interviewed_r2"),
      interview_scheduled_r2:     sum("interview_scheduled_r2"),
      during_interview_back_out_r2: sum("during_interview_back_out_r2"),
      feedback_pending_r2:        sum("feedback_pending_r2"),
      rejected_r2:                sum("rejected_r2"),

      // R3
      yet_to_be_interviewed_r3:   sum("yet_to_be_interviewed_r3"),
      interview_scheduled_r3:     sum("interview_scheduled_r3"),
      during_interview_back_out_r3: sum("during_interview_back_out_r3"),
      feedback_pending_r3:        sum("feedback_pending_r3"),
      rejected_r3:                sum("rejected_r3"),

      // R4
      yet_to_be_interviewed_r4:   sum("yet_to_be_interviewed_r4"),
      interview_scheduled_r4:     sum("interview_scheduled_r4"),
      during_interview_back_out_r4: sum("during_interview_back_out_r4"),
      feedback_pending_r4:        sum("feedback_pending_r4"),
      rejected_r4:                sum("rejected_r4"),

      // Final stage
      final_select:               sum("final_select"),
      final_reject:               sum("final_reject"),
      pre_offer_back_out:         sum("pre_offer_back_out"),

      // Offer stage
      offer_released:             sum("offer_released"),
      offer_yet_to_be_released:   sum("offer_yet_to_be_released"),
      post_offer_back_out:        sum("post_offer_back_out"),

      // Joining stage
      offer_accepted_yet_to_join: sum("offer_accepted_yet_to_join"),
      offer_accepted_joined:      sum("offer_accepted_joined"),
      offer_accepted_not_joined:  sum("offer_accepted_not_joined"),
      offer_rejected_not_joined:  sum("offer_rejected_not_joined"),
      profile_reshared:           sum("profile_reshared"),

      // Funnel cumulative counts
      screen_select_funnel:       sum("screen_select_funnel"),
      selected_r1_funnel:         sum("selected_r1_funnel"),
      selected_r2_funnel:         sum("selected_r2_funnel"),
      selected_r3_funnel:         sum("selected_r3_funnel"),
      selected_r4_funnel:         sum("selected_r4_funnel"),
      final_select_funnel:        sum("final_select_funnel"),
      offer_released_funnel:      sum("offer_released_funnel"),
      joined_funnel:              sum("joined_funnel"),
    }),
    [filteredData],
  );

  const sourcedPerOpening = useMemo(() => {
    const openings = Number(G?.openings || 0);
    const sourced = Number(G?.profile_sourced || 0);
    if (!openings) return 0;
    return (sourced / openings).toFixed(1);
  }, [G.openings, G.profile_sourced]);

  /* ── derived / computed stage values ── */
  const profilesAvailableForScreening =
    G.sent_to_client -
    G.duplicate -
    G.change_in_requirement -
    G.position_on_hold_closed;

  /* ── stage definitions ── */
  const stages = [
    {
      id: "sourcing",
      icon: "🔍",
      title: "Sourcing",
      badge: "Stage 1",
      accent: "#4F46E5",
      light: true,
      mainNum: G.profile_sourced,
      mainLabel: "Profiles Sourced",
      deductions: [
        {
          label: "Not Assessed",
          val: G.profile_sourced - G.assessed,
          color: "#94A3B8",
        },
      ],
      result: G.assessed,
      resultLabel: "Assessed",
      convValue: sourcedPerOpening,
      convLabel: "Sourced / Opening",
    },
    {
      id: "assessed",
      icon: "✅",
      title: "Assessment",
      badge: "Stage 2",
      accent: "#7C3AED",
      light: true,
      mainNum: G.assessed,
      mainLabel: "Assessed",
      deductions: [
        {
          label: "Not Cleared",
          val: G.assessed - G.assessed_cleared,
          color: "#94A3B8",
        },
      ],
      result: G.assessed_cleared,
      resultLabel: "Assessed & Cleared",
      convFrom: G.profile_sourced,
      convLabel: "of Sourced",
    },
    {
      id: "sent",
      icon: "📤",
      title: "Sent to Client",
      badge: "Stage 3",
      accent: "#0EA5E9",
      light: true,
      mainNum: G.sent_to_client,
      mainLabel: "Profiles Sent",
      deductions: [
        { label: "Duplicate",            val: G.duplicate,               color: "#F59E0B" },
        { label: "Requirement Changed",  val: G.change_in_requirement,   color: "#EF4444" },
        { label: "Hold / Closed",        val: G.position_on_hold_closed, color: "#EF4444" },
      ],
      result: profilesAvailableForScreening,
      resultLabel: "Available for Screening",
      convFrom: G.assessed_cleared,
      convLabel: "of Cleared",
    },
    {
      id: "screening",
      icon: "🎯",
      title: "Screening",
      badge: "Stage 4",
      accent: "#F59E0B",
      light: false,
      mainNum: profilesAvailableForScreening,
      mainLabel: "Available",
      deductions: [
        { label: "Screen Reject",         val: G.screen_reject,            color: "#EF4444" },
        { label: "Yet to Shortlist",      val: G.yet_to_be_shortlisted,    color: "#F59E0B" },
        { label: "Pre-Interview Back Out", val: G.pre_interview_back_out,  color: "#EF4444" },
        { label: "Profile Reshared",     val: G.profile_reshared,        color: "#94A3B8" },
      ],
      result: G.screen_select_funnel,
      resultLabel: "Reached R1",
      convFrom: profilesAvailableForScreening,
      convLabel: "pass rate",
    },
    {
      id: "r1",
      icon: "🏅",
      title: "Round 1",
      badge: "Interview",
      accent: "#8B5CF6",
      light: true,
      mainNum: G.screen_select_funnel,
      mainLabel: "Input",
      deductions: [
        { label: "Screen Select (Awaiting R1)", val: G.screen_select,                color: "#22C55E" },
        { label: "Scheduled",                   val: G.interview_scheduled_r1,       color: "#60A5FA" },
        { label: "Back Out (During)",            val: G.during_interview_back_out_r1, color: "#EF4444" },
        { label: "Feedback Pending",             val: G.feedback_pending_r1,          color: "#94A3B8" },
        { label: "Rejected",                     val: G.rejected_r1,                  color: "#EF4444" },
      ],
      result: G.selected_r1_funnel,
      resultLabel: "Reached R2",
      convFrom: G.screen_select_funnel,
      convLabel: "pass rate",
    },
    {
      id: "r2",
      icon: "🥈",
      title: "Round 2",
      badge: "Interview",
      accent: "#3B82F6",
      light: true,
      mainNum: G.selected_r1_funnel,
      mainLabel: "Input",
      deductions: [
        { label: "Yet to Interview",  val: G.yet_to_be_interviewed_r2,    color: "#F59E0B" },
        { label: "Scheduled",         val: G.interview_scheduled_r2,       color: "#60A5FA" },
        { label: "Back Out (During)", val: G.during_interview_back_out_r2, color: "#EF4444" },
        { label: "Feedback Pending",  val: G.feedback_pending_r2,          color: "#94A3B8" },
        { label: "Rejected",          val: G.rejected_r2,                  color: "#EF4444" },
      ],
      result: G.selected_r2_funnel,
      resultLabel: "Reached R3",
      convFrom: G.selected_r1_funnel,
      convLabel: "pass rate",
    },
    {
      id: "r3",
      icon: "🥉",
      title: "Round 3",
      badge: "Interview",
      accent: "#06B6D4",
      light: true,
      mainNum: G.selected_r2_funnel,
      mainLabel: "Input",
      deductions: [
        { label: "Yet to Interview",  val: G.yet_to_be_interviewed_r3,    color: "#F59E0B" },
        { label: "Scheduled",         val: G.interview_scheduled_r3,       color: "#60A5FA" },
        { label: "Back Out (During)", val: G.during_interview_back_out_r3, color: "#EF4444" },
        { label: "Feedback Pending",  val: G.feedback_pending_r3,          color: "#94A3B8" },
        { label: "Rejected",          val: G.rejected_r3,                  color: "#EF4444" },
      ],
      result: G.selected_r3_funnel,
      resultLabel: "Reached R4",
      convFrom: G.selected_r2_funnel,
      convLabel: "pass rate",
    },
    {
      id: "r4",
      icon: "🏆",
      title: "Round 4",
      badge: "Interview",
      accent: "#10B981",
      light: true,
      mainNum: G.selected_r3_funnel,
      mainLabel: "Input",
      deductions: [
        { label: "Yet to Interview",  val: G.yet_to_be_interviewed_r4,    color: "#F59E0B" },
        { label: "Scheduled",         val: G.interview_scheduled_r4,       color: "#60A5FA" },
        { label: "Back Out (During)", val: G.during_interview_back_out_r4, color: "#EF4444" },
        { label: "Feedback Pending",  val: G.feedback_pending_r4,          color: "#94A3B8" },
        { label: "Rejected",          val: G.rejected_r4,                  color: "#EF4444" },
      ],
      result: G.selected_r4_funnel,
      resultLabel: "Reached Final Stage",
      convFrom: G.selected_r3_funnel,
      convLabel: "pass rate",
    },
    {
      id: "final",
      icon: "🎖️",
      title: "Final Stage",
      badge: "Outcome",
      accent: "#059669",
      light: true,
      mainNum: G.selected_r4_funnel,
      mainLabel: "Input",
      deductions: [
        { label: "Final Select",       val: G.final_select,      color: "#EF4444" },
        { label: "Post Interview Back Out", val: G.post_interview_back_out, color: "#EF4444" },
        { label: "Final Reject",       val: G.final_reject,      color: "#EF4444" },
        { label: "Pre Offer Back Out", val: G.pre_offer_back_out, color: "#EF4444" },
      ],
      result: G.final_select_funnel,
      resultLabel: "Moved to Offer",
      convFrom: G.screen_select_funnel,
      convLabel: "of Screened",
    },
    {
      id: "offer",
      icon: "📋",
      title: "Offer Stage",
      badge: "Offer",
      accent: "#F97316",
      light: true,
      mainNum: G.final_select_funnel,
      mainLabel: "Input",
      deductions: [
        { label: "Yet to Release",    val: G.offer_yet_to_be_released, color: "#F59E0B" },
        // { label: "Post Offer Back Out", val: G.post_offer_back_out,    color: "#EF4444" },
      ],
      result: G.offer_released_funnel,
      resultLabel: "Moved to Joining",
      convFrom: G.final_select_funnel,
      convLabel: "release rate",
    },
    {
      id: "joining",
      icon: "🚀",
      title: "Joining",
      badge: "Final",
      accent: "#10B981",
      light: true,
      mainNum: G.offer_released_funnel,
      mainLabel: "Input",
      deductions: [
        { label: "Post-Offer Back Out",   val: G.post_offer_back_out,        color: "#EF4444" },
        { label: "Offer Rejected",        val: G.offer_rejected_not_joined,  color: "#EF4444" },
        { label: "Accepted – Not Joined", val: G.offer_accepted_not_joined,  color: "#F59E0B" },
        { label: "Yet to Join",           val: G.offer_accepted_yet_to_join, color: "#F59E0B" },
      ],
      result: G.joined_funnel,
      resultLabel: "Joined",
      convFrom: G.offer_released_funnel,
      convLabel: "join rate",
    },
  ];

  /* ── per-account breakdown for a stage ── */
  const getAccountBreakdown = (stageId) => {
    const keyMap = {
      sourcing:  "profile_sourced",
      assessed:  "assessed_cleared",
      sent:      "sent_to_client",
      screening: "screen_select_funnel",
      r1:        "selected_r1_funnel",
      r2:        "selected_r2_funnel",
      r3:        "selected_r3_funnel",
      r4:        "selected_r4_funnel",
      final:     "final_select_funnel",
      offer:     "offer_released_funnel",
      joining:   "joined_funnel",
    };
    const key = keyMap[stageId];
    if (!key) return [];
    const map = {};
    filteredData.forEach((r) => {
      const acc = r.account || "Unknown";
      map[acc] = (map[acc] || 0) + n(r[key]);
    });
    return Object.entries(map)
      .map(([account, val]) => ({ account, val }))
      .sort((a, b) => b.val - a.val)
      .slice(0, 6);
  };

  /* ── top KPI cards ── */
  const kpis = [
    {
      label: "Open Positions",
      sub: "Total Openings",
      val: G.openings,
      accent: "#4F46E5",
      idx: 0,
    },
    {
      label: "Profiles Sourced",
      sub: "Pipeline Intake",
      val: G.profile_sourced,
      accent: "#7C3AED",
      idx: 1,
    },
    {
      label: "Sent to Client",
      sub: "Client Submissions",
      val: G.sent_to_client,
      accent: "#0EA5E9",
      idx: 2,
    },
    {
      label: "Screen Select",
      sub: "Cleared Screening",
      val: G.screen_select_funnel,
      accent: "#F59E0B",
      idx: 3,
    },
    {
      label: "Final Select",
      sub: "Cleared All Rounds",
      val: G.final_select_funnel,
      accent: "#10B981",
      idx: 4,
    },
    {
      label: "Joined",
      sub: "Successful Hires",
      val: G.joined_funnel,
      accent: "#059669",
      idx: 5,
    },
  ];

  const applyRange = (mode) => {
    setRangeMode(mode);
    const today = todayYMD();
    const yesterday = addDays(-1);
    if (mode === "today")     { setFromDate(today);        setToDate(today); }
    if (mode === "yesterday") { setFromDate(yesterday);    setToDate(yesterday); }
    if (mode === "3days")     { setFromDate(addDays(-2));  setToDate(today); }
    if (mode === "week")      { setFromDate(startOfWeek()); setToDate(today); }
    if (mode === "month")     { setFromDate(startOfMonth()); setToDate(today); }
  };

  const isCustom = rangeMode === "custom";
  const MIN_DATE = dayjs("2026-04-22").startOf("day");
  const disableFutureDates = (current) =>
  current && (current > dayjs().endOf("day") || current < MIN_DATE);
  const dateLabel =
    fromDate === toDate ? fmt(fromDate) : `${fmt(fromDate)} – ${fmt(toDate)}`;

  const toggleStage = (id) =>
    setExpandedStage((prev) => (prev === id ? null : id));

  const handleExport = () => {
    exportToXLSX(
      filteredData,
      `recruitment_dashboard_${fromDate}_to_${toDate}.xlsx`,
    );
  };

  return (
    <>
      <GlobalFonts />
      <MainDiv>
        <Sidemenu />
        <ContentWrapDiv>
          {/* ── HEADER ── */}
          <TopContainer>
            <div className="left">
              <div className="page-title">Client Dashboard</div>
              <div className="date-label">Showing: {dateLabel}</div>
            </div>
            <div className="right">
              <RangeTabsWrapper>
                {[
                  { key: "today",     label: "Today" },
                  { key: "yesterday", label: "Yesterday" },
                  { key: "3days",     label: "Last 3 Days" },
                  { key: "week",      label: "This Week" },
                  { key: "month",     label: "This Month" },
                  { key: "custom",    label: "Custom" },
                ].map((t) => (
                  <RangeTab
                    key={t.key}
                    active={rangeMode === t.key}
                    onClick={() => applyRange(t.key)}
                  >
                    {t.label}
                  </RangeTab>
                ))}
              </RangeTabsWrapper>
            </div>
          </TopContainer>

          {/* ── FILTERS ROW 1: dropdowns + export ── */}
          <FilterRow>
            {admin && (
              <StyledSelectWrap>
                <Select
                  showSearch
                  placeholder="All recruiters"
                  value={filterRecruiter}
                  onChange={setFilterRecruiter}
                  allowClear
                  style={{ width: 190 }}
                >
                  {recruiterOptions.map((r) => {
                    const p = recruiterColorMap[r] || RECRUITER_PALETTE[0];
                    return (
                      <Select.Option key={r} value={r}>
                        <FilterRecruiterChip bg={p.bg} fg={p.fg}>
                          {r}
                        </FilterRecruiterChip>
                      </Select.Option>
                    );
                  })}
                </Select>
              </StyledSelectWrap>
            )}
            <StyledSelectWrap>
              <Select
                showSearch
                placeholder="All Accounts"
                value={filterAccount}
                onChange={setFilterAccount}
                allowClear
                style={{ width: 180 }}
              >
                {accountOptions.map((a) => {
                  const cc = clientColor(a);
                  return (
                    <Select.Option key={a} value={a}>
                      <FilterClientChip bg={cc.bg} fg={cc.fg}>
                        {a}
                      </FilterClientChip>
                    </Select.Option>
                  );
                })}
              </Select>
            </StyledSelectWrap>
            <StyledSelectWrap>
              <Select
                showSearch
                placeholder="All Sub Accounts"
                value={filterSubAccount}
                onChange={setFilterSubAccount}
                allowClear
                style={{ width: 190 }}
              >
                {subAccountOptions.map((s) => {
                  const cc = clientColor(s);
                  return (
                    <Select.Option key={s} value={s}>
                      <FilterClientChip bg={cc.bg} fg={cc.fg}>
                        {s}
                      </FilterClientChip>
                    </Select.Option>
                  );
                })}
              </Select>
            </StyledSelectWrap>
            <StyledSelectWrap>
              <Select
                showSearch
                placeholder="All Roles"
                value={filterRole}
                onChange={setFilterRole}
                allowClear
                style={{ width: 180 }}
              >
                {roleOptions.map((r) => {
                  const cc = clientColor(r);
                  return (
                    <Select.Option key={r} value={r}>
                      <FilterRoleChip bg={cc.bg} fg={cc.fg}>
                        {r}
                      </FilterRoleChip>
                    </Select.Option>
                  );
                })}
              </Select>
            </StyledSelectWrap>
            <StyledSelectWrap>
              <Select
                showSearch
                placeholder="All End Clients"
                value={filterEndClient}
                onChange={setFilterEndClient}
                allowClear
                style={{ width: 180 }}
              >
                {endClientOptions.map((c) => {
                  const cc = clientColor(c);
                  return (
                    <Select.Option key={c} value={c}>
                      <FilterClientChip bg={cc.bg} fg={cc.fg}>
                        {c}
                      </FilterClientChip>
                    </Select.Option>
                  );
                })}
              </Select>
            </StyledSelectWrap>

            {/* EXPORT BUTTON — always at end of row 1 */}
            <ExportBtn onClick={handleExport} title="Export to Excel">
              ↓ Export Excel
            </ExportBtn>
          </FilterRow>

          {/* ── FILTERS ROW 2: date pickers — only shown in custom mode, always on own line ── */}
          {isCustom && (
            <FilterRow>
              <span style={{ fontSize: 12, fontWeight: 600, color: "#94A3B8", alignSelf: "center", marginRight: 4 }}>
                Date Range:
              </span>
              <StyledDatePickerWrap>
                <DatePicker
                  placeholder="From date"
                  value={fromDate ? dayjs(fromDate) : null}
                  format="DD MMM YYYY"
                  allowClear={false}
                  disabledDate={disableFutureDates}
                  onChange={(d) => {
                    if (d) setFromDate(d.format("YYYY-MM-DD"));
                  }}
                  style={{ width: 170 }}
                />
              </StyledDatePickerWrap>
              <span className="date-sep" style={{ alignSelf: "center" }}>→</span>
              <StyledDatePickerWrap>
                <DatePicker
                  placeholder="To date"
                  value={toDate ? dayjs(toDate) : null}
                  format="DD MMM YYYY"
                  allowClear={false}
                  disabledDate={disableFutureDates}
                  onChange={(d) => {
                    if (d) setToDate(d.format("YYYY-MM-DD"));
                  }}
                  style={{ width: 170 }}
                />
              </StyledDatePickerWrap>
            </FilterRow>
          )}

          {/* ── MAIN CONTENT ── */}
          <DashScrollArea>
            {loading ? (
              <SpinWrap>
                <Spin size="large" />
              </SpinWrap>
            ) : filteredData.length === 0 ? (
              <EmptyWrap>
                <div className="icon">📋</div>
                <div>No data found for the selected range</div>
              </EmptyWrap>
            ) : (
              <>
                {/* KPI STRIP */}
                <SectionTitle>Key Metrics</SectionTitle>
                <KPIStrip>
                  {kpis.map((k) => (
                    <KPICard key={k.label} accent={k.accent} idx={k.idx}>
                      <KPIValue accent={k.accent}>
                        {k.val === 0 ? "—" : k.val}
                      </KPIValue>
                      <KPILabel>{k.label}</KPILabel>
                      <KPISubLabel>{k.sub}</KPISubLabel>
                    </KPICard>
                  ))}
                </KPIStrip>

                {/* END-TO-END CONVERSION */}
                <SectionTitle>End-to-End Conversion</SectionTitle>
                <KPIStrip>
                  {[
                    {
                      label: "Assessed → Profile Sourced",
                      num: G.assessed,
                      den: G.profile_sourced,
                      accent: "#4F46E5",
                      idx: 0,
                    },
                    {
                      label: "Screen Select → Profile Sent",
                      num: G.screen_select_funnel,
                      den: G.sent_to_client,
                      accent: "#0EA5E9",
                      idx: 1,
                    },
                    {
                      label: "R1 Select → Screen Select",
                      num: G.selected_r1_funnel,
                      den: G.screen_select_funnel,
                      accent: "#8B5CF6",
                      idx: 2,
                    },
                    {
                      label: "Final Select → R1 Select",
                      num: G.final_select_funnel,
                      den: G.selected_r1_funnel,
                      accent: "#F59E0B",
                      idx: 3,
                    },
                    {
                      label: "Joined → Offered",
                      num: G.joined_funnel,
                      den: G.offer_released_funnel,
                      accent: "#10B981",
                      idx: 4,
                    },
                    {
                      label: "Joined → Profile Sent",
                      num: G.joined_funnel,
                      den: G.sent_to_client,
                      accent: "#059669",
                      idx: 5,
                      decimal: true,
                    },
                  ].map((k) => {
                    const ratio = k.den > 0 ? k.num / k.den : 0;
                    const display =
                      k.num === 0 || k.den === 0
                        ? "—"
                        : k.decimal
                          ? (ratio * 100).toFixed(2) + "%"
                          : pct(k.num, k.den);
                    return (
                      <KPICard key={k.label} accent={k.accent} idx={k.idx}>
                        <KPIValue accent={k.accent}>{display}</KPIValue>
                        <KPILabel>{k.label}</KPILabel>
                        <KPISubLabel>{k.sub}</KPISubLabel>
                        <KPICountLabel>
                          {k.num} of {k.den}
                        </KPICountLabel>
                      </KPICard>
                    );
                  })}
                </KPIStrip>

                {/* FUNNEL STAGES */}
                <SectionTitle>
                  Recruitment Funnel — click any card to drill down
                </SectionTitle>
                <FunnelGrid>
                  {stages.map((stage, idx) => {
                    const isExpanded = expandedStage === stage.id;
                    const breakdown = isExpanded
                      ? getAccountBreakdown(stage.id)
                      : [];
                    const convPct = pct(stage.result, stage.convFrom);

                    return (
                      <React.Fragment key={stage.id}>
                        <StageCard
                          accent={stage.accent}
                          idx={idx}
                          onClick={() => toggleStage(stage.id)}
                          style={
                            isExpanded
                              ? {
                                  borderBottomLeftRadius: 0,
                                  borderBottomRightRadius: 0,
                                  borderBottom: "none",
                                }
                              : {}
                          }
                        >
                          <StageHeader accent={stage.accent}>
                            <StageIcon>{stage.icon}</StageIcon>
                            <StageTitle light={stage.light}>
                              {stage.title}
                            </StageTitle>
                            <StageBadge light={stage.light}>
                              {stage.badge}
                            </StageBadge>
                          </StageHeader>
                          <StageBody>
                            <StageMainNum accent={stage.accent}>
                              {stage.mainNum}
                            </StageMainNum>
                            <StageMainLabel>{stage.mainLabel}</StageMainLabel>

                            <StageDivider />

                            {stage.deductions.filter((d) => Number(d.val) > 0)
                              .length > 0 && (
                              <StageDeductions>
                                {stage.deductions
                                  .filter((d) => Number(d.val) > 0)
                                  .map((d, di) => (
                                    <DeductRow key={di}>
                                      <DeductLabel color={d.color}>
                                        {d.label}
                                      </DeductLabel>
                                      <DeductVal color={d.color}>
                                        {d.val}
                                      </DeductVal>
                                    </DeductRow>
                                  ))}
                              </StageDeductions>
                            )}

                            <StageBottom>
                              <StageDivider style={{ margin: 0 }} />
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "baseline",
                                  gap: 8,
                                }}
                              >
                                <span
                                  style={{
                                    fontFamily: "'Syne',sans-serif",
                                    fontSize: 28,
                                    fontWeight: 800,
                                    color: stage.accent,
                                    fontVariantNumeric: "tabular-nums",
                                  }}
                                >
                                  {stage.result}
                                </span>
                                <span
                                  style={{
                                    fontSize: 11,
                                    color: "#94A3B8",
                                    fontWeight: 600,
                                  }}
                                >
                                  {stage.resultLabel}
                                </span>
                              </div>
                              <StageConversion accent={stage.accent}>
                                <ConvLabel>{stage.convLabel}</ConvLabel>
                                <ConvValue accent={stage.accent}>
                                  {stage.convValue !== undefined
                                    ? stage.convValue
                                    : convPct}
                                </ConvValue>
                              </StageConversion>
                            </StageBottom>
                          </StageBody>
                        </StageCard>

                        {isExpanded && (
                          <DetailPanel>
                            <DetailTitle>
                              {stage.title} — by Account ({stage.resultLabel})
                            </DetailTitle>
                            {breakdown.length === 0 ? (
                              <div style={{ color: "#94A3B8", fontSize: 13 }}>
                                No breakdown available
                              </div>
                            ) : (
                              <AccountList>
                                {breakdown.map(({ account, val }) => {
                                  const cc = clientColor(account);
                                  return (
                                    <AccountItem
                                      key={account}
                                      accent={stage.accent}
                                    >
                                      <AccountName>
                                        <span
                                          style={{
                                            display: "inline-block",
                                            padding: "2px 9px",
                                            borderRadius: 6,
                                            background: cc.bg,
                                            color: cc.fg,
                                            fontSize: 11,
                                            fontWeight: 700,
                                          }}
                                        >
                                          {account}
                                        </span>
                                      </AccountName>
                                      <AccountVal accent={stage.accent}>
                                        {val}
                                      </AccountVal>
                                    </AccountItem>
                                  );
                                })}
                              </AccountList>
                            )}
                          </DetailPanel>
                        )}
                      </React.Fragment>
                    );
                  })}
                </FunnelGrid>
              </>
            )}
          </DashScrollArea>
        </ContentWrapDiv>
      </MainDiv>
    </>
  );
};

export default ClientDashboard;